/**
 * Where a piece label's QR leads: /scan/l/:uuid. CR-2026-PIECE-LABELS (2026-10-14).
 * Resolves the label (GET /api/labels/scan/:uuid; a voided label is refused and logged by the server) and shows the
 * physical piece with the heats issued to it (GET /api/piece-trace/pieces/:publicId).
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { Alert, Chip, Container, Paper, Stack, Typography } from "@mui/material";
import ApiCall from "../../utils/apicall";

const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;

export default function ScanLabel() {
  const { uuid } = useParams();
  const [piece, setPiece] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await ApiCall.get(`/labels/scan/${uuid}`);
        const d = r.data;
        if (d?.voided) { setMsg({ severity: "error", text: "This label is voided. Do not use it. Print the replacement label." }); return; }
        if (d?.data?.parentObjectType !== "UNIT_PIECE") { setMsg({ severity: "info", text: `This label is for a ${String(d?.data?.parentObjectType || "record").toLowerCase()}, not a piece.` }); return; }
        const p = await ApiCall.get(`/piece-trace/pieces/${encodeURIComponent(d.data.parentObject.piece_public_id)}`);
        setPiece(p.data.data);
      } catch (e) { setMsg({ severity: "error", text: msgOf(e, "This QR code is not registered in SFM.") }); }
    })();
  }, [uuid]);

  return (
    <>
      <Helmet><title>Scan | SFM</title></Helmet>
      <Container maxWidth="sm" sx={{ pt: 3 }}>
        {msg && <Alert severity={msg.severity}>{msg.text}</Alert>}
        {piece && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h3" sx={{ fontWeight: 800 }}>{piece.mark}</Typography>
            <Typography variant="h5">{piece.display}</Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", mb: 2 }}>{piece.publicId}</Typography>
            <Typography variant="subtitle2">Heats issued</Typography>
            {piece.heats.length === 0 ? <Alert severity="error" sx={{ mt: 1 }}>No heat issued to this piece.</Alert> : (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
                {piece.heats.map((h) => <Chip key={h.heatId} label={`${h.heatNumber}${h.confirmedAtCut ? " (confirmed at cut)" : ""}`} />)}
              </Stack>
            )}
          </Paper>
        )}
      </Container>
    </>
  );
}
